import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Star, MapPin, Phone, Check, Mail, Search, Menu } from "lucide-react";

// Kinetic parallax hero mockup — pure HTML/CSS devices with framer-motion
// scroll-linked depth layering.
export default function HeroMockup() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const springCfg = { stiffness: 90, damping: 22, mass: 0.6 };

  // Different parallax speeds for different layers
  const yWeb = useSpring(useTransform(scrollYProgress, [0, 1], [40, -80]), springCfg);
  const yPhone = useSpring(useTransform(scrollYProgress, [0, 1], [60, -140]), springCfg);
  const yCard = useSpring(useTransform(scrollYProgress, [0, 1], [10, -40]), springCfg);
  const yList = useSpring(useTransform(scrollYProgress, [0, 1], [30, -110]), springCfg);
  const yGoogle = useSpring(useTransform(scrollYProgress, [0, 1], [80, -160]), springCfg);
  const yEmail = useSpring(useTransform(scrollYProgress, [0, 1], [20, -60]), springCfg);

  return (
    <div
      ref={ref}
      data-testid="hero-mockup-composition"
      className="relative w-full aspect-[4/3] md:aspect-[5/4] lg:aspect-[4/3]"
    >
      {/* Gold radial glow */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 65% 40%, rgba(200,162,75,0.14), transparent 55%)",
        }}
      />
      {/* Grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#C8A24B 1px, transparent 1px), linear-gradient(90deg, #C8A24B 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Listing template */}
      <motion.div
        style={{ y: yList, rotate: -6 }}
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-[4%] left-[2%] w-[30%] aspect-[3/4] bg-[#0e0e0e] border border-[#27272a] shadow-2xl"
      >
        <div className="p-3 h-full flex flex-col">
          <div className="text-[8px] tracking-[0.3em] text-[#C8A24B] uppercase">
            Just Listed
          </div>
          <div className="mt-1 flex-1 relative overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 60%, #0a0a0a 100%)",
              }}
            />
            <div className="absolute bottom-2 left-2 right-2">
              <div className="h-1 w-8 bg-[#C8A24B]" />
              <div className="h-1.5 mt-1.5 bg-white/80 w-[60%]" />
              <div className="h-1 mt-1 bg-white/40 w-[80%]" />
            </div>
          </div>
          <div className="mt-2 space-y-1">
            <div className="h-1 bg-[#C8A24B] w-1/3" />
            <div className="h-1 bg-white/50 w-2/3" />
            <div className="h-1 bg-white/30 w-1/2" />
          </div>
        </div>
      </motion.div>

      {/* Website */}
      <motion.div
        style={{ y: yWeb, rotate: -2 }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-[10%] left-[18%] w-[64%] aspect-[16/10] bg-[#0e0e0e] border border-[#27272a] shadow-2xl rounded-sm z-10 overflow-hidden"
      >
        <div className="h-6 bg-[#131313] border-b border-[#27272a] flex items-center px-2 gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-[#3f3f46]" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#3f3f46]" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#3f3f46]" />
          <div className="ml-4 flex-1 h-3 bg-[#0a0a0a] border border-[#27272a] rounded-sm flex items-center px-2">
            <Search size={7} className="text-[#52525b]" />
            <span className="text-[7px] text-[#71717a] ml-1.5 tracking-wider">
              agentmark.ca
            </span>
          </div>
        </div>
        <div className="p-4 h-[calc(100%-1.5rem)] flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <div
                  className="w-3 h-3 border border-[#C8A24B]"
                  style={{ clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }}
                />
                <div className="h-1.5 w-10 bg-white/80" />
              </div>
              <div className="flex gap-2">
                <div className="h-1 w-4 bg-white/40" />
                <div className="h-1 w-4 bg-white/40" />
                <div className="h-1 w-4 bg-white/40" />
                <div className="h-1 w-6 bg-[#C8A24B]" />
              </div>
            </div>
            <div className="mt-6">
              <div className="h-1 w-16 bg-[#C8A24B]" />
              <div className="mt-2 space-y-1">
                <div className="h-2.5 bg-white w-[70%]" />
                <div className="h-2.5 bg-white w-[55%]" />
              </div>
              <div className="mt-3 h-1 bg-white/30 w-[60%]" />
              <div className="mt-1 h-1 bg-white/30 w-[50%]" />
              <div className="mt-4 flex gap-1.5">
                <div className="h-3 w-14 bg-[#C8A24B]" />
                <div className="h-3 w-14 border border-white/30" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <div className="aspect-video bg-[#1a1a1a] border border-[#27272a]" />
            <div className="aspect-video bg-[#1a1a1a] border border-[#27272a]" />
            <div className="aspect-video bg-[#1a1a1a] border border-[#27272a]" />
          </div>
        </div>
      </motion.div>

      {/* Google Profile */}
      <motion.div
        style={{ y: yGoogle, rotate: 4 }}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-[8%] right-[4%] w-[30%] bg-white text-[#111] shadow-2xl z-20 overflow-hidden rounded-sm"
      >
        <div className="p-2.5">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-bold">
              <span className="text-[#4285F4]">G</span>
              <span className="text-[#EA4335]">o</span>
              <span className="text-[#FBBC05]">o</span>
              <span className="text-[#4285F4]">g</span>
              <span className="text-[#34A853]">l</span>
              <span className="text-[#EA4335]">e</span>
            </span>
            <span className="text-[7px] text-[#5f6368] tracking-wider">
              Business
            </span>
          </div>
          <div className="mt-2 flex items-start gap-2">
            <div className="w-8 h-8 bg-[#0a0a0a] flex items-center justify-center flex-shrink-0">
              <div
                className="w-3 h-3 border border-[#C8A24B]"
                style={{ clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[9px] font-semibold text-[#202124]">
                Hedges Realty
              </div>
              <div className="text-[7px] text-[#5f6368]">Real estate agency</div>
              <div className="flex items-center gap-0.5 mt-0.5">
                <span className="text-[7px] text-[#202124] font-medium">
                  5.0
                </span>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    size={5}
                    fill="#FBBC05"
                    className="text-[#FBBC05]"
                  />
                ))}
                <span className="text-[6px] text-[#5f6368] ml-0.5">(48)</span>
              </div>
            </div>
          </div>
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-1">
              <MapPin size={7} className="text-[#5f6368]" />
              <div className="h-0.5 w-14 bg-[#e0e0e0]" />
            </div>
            <div className="flex items-center gap-1">
              <Phone size={7} className="text-[#5f6368]" />
              <div className="h-0.5 w-10 bg-[#e0e0e0]" />
            </div>
          </div>
          <div className="mt-2 flex gap-1">
            <div className="h-3 flex-1 bg-[#1a73e8] text-[6px] text-white flex items-center justify-center rounded-sm">
              Directions
            </div>
            <div className="h-3 flex-1 border border-[#e0e0e0] text-[6px] text-[#1a73e8] flex items-center justify-center rounded-sm">
              Website
            </div>
          </div>
        </div>
      </motion.div>

      {/* Phone */}
      <motion.div
        style={{ y: yPhone, rotate: 6 }}
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-[12%] bottom-[6%] w-[18%] aspect-[9/19] bg-black border-[3px] border-[#1a1a1a] rounded-[1.2rem] shadow-2xl z-30 overflow-hidden"
      >
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-8 h-1 bg-[#1a1a1a] rounded-full z-10" />
        <div className="h-full bg-[#0a0a0a] p-1.5 pt-4 flex flex-col">
          <div className="flex items-center justify-between px-1">
            <Menu size={6} className="text-white/60" />
            <div
              className="w-2.5 h-2.5 border border-[#C8A24B]"
              style={{ clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }}
            />
            <div className="w-1 h-1 rounded-full bg-[#C8A24B]" />
          </div>
          <div className="mt-3 flex-1">
            <div className="h-0.5 w-6 bg-[#C8A24B]" />
            <div className="mt-1 space-y-0.5">
              <div className="h-1.5 bg-white w-full" />
              <div className="h-1.5 bg-white w-[70%]" />
            </div>
            <div className="mt-2 h-4 bg-[#C8A24B] rounded-sm" />
            <div className="mt-2 grid grid-cols-2 gap-0.5">
              <div className="aspect-square bg-[#1a1a1a]" />
              <div className="aspect-square bg-[#1a1a1a]" />
              <div className="aspect-square bg-[#1a1a1a]" />
              <div className="aspect-square bg-[#1a1a1a]" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Business card */}
      <motion.div
        style={{ y: yCard, rotate: -12 }}
        initial={{ opacity: 0, y: 40, rotate: -20 }}
        animate={{ opacity: 1, y: 0, rotate: -12 }}
        transition={{ duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-[8%] left-[8%] w-[24%] aspect-[7/4] bg-[#0a0a0a] border border-[#27272a] shadow-2xl z-40 overflow-hidden"
      >
        <div className="p-2.5 h-full flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div
              className="w-4 h-4 border border-[#C8A24B]"
              style={{ clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }}
            />
            <div className="text-right">
              <div className="text-[7px] font-heading tracking-widest text-white">
                AGENT
              </div>
              <div className="text-[6px] font-heading tracking-widest text-[#C8A24B]">
                MARKETING
              </div>
            </div>
          </div>
          <div>
            <div className="text-[8px] text-white font-medium">Dalen Hedges</div>
            <div className="text-[6px] text-[#71717a] tracking-wider">
              REAL ESTATE
            </div>
            <div className="mt-1 flex items-center gap-1">
              <div className="h-[1px] w-3 bg-[#C8A24B]" />
              <div className="text-[6px] text-[#a1a1aa]">hedges.ca</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Email pill */}
      <motion.div
        style={{ y: yEmail, rotate: 1 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.0, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-[2%] left-1/2 -translate-x-1/2 w-[46%] bg-[#0e0e0e] border border-[#27272a] shadow-xl z-20 rounded-sm overflow-hidden"
      >
        <div className="flex items-center gap-2 p-1.5">
          <div className="w-6 h-6 bg-[#1a1a1a] border border-[#27272a] flex items-center justify-center flex-shrink-0">
            <Mail size={9} className="text-[#C8A24B]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[8px] text-white font-medium">
              dalen@hedges.ca
            </div>
            <div className="text-[6px] text-[#71717a]">
              Branded business email
            </div>
          </div>
          <Check size={9} className="text-[#C8A24B] flex-shrink-0" />
        </div>
      </motion.div>
    </div>
  );
}
